import {Banner, Button, Counter, Switch, TextField} from '@wandb/components';
import {TargetBlank} from '@wandb/weave/common/util/links';
import React, {useState} from 'react';

import {DD_PRIVACY_ALLOW} from '../../../util/datadog/privacy';
import DOC_URLS from '../../../util/doc_urls';
import {PANEL_BANK_CHARTS_NAME} from '../../../util/panelbankTypes';
import {maybePluralize} from '../../../util/uihelpers';
import {InstrumentedWaveLoader} from '../../utility/InstrumentedLoader';
import {useShouldAutoGeneratePanels} from '../Settings/hooks';
import {BULK_ADD_LIMIT_MULTI_METRIC} from './constants';
import {useCreateMultiMetricPanels} from './hooks/useCreateMultiMetricPanels';
import {useMultiMetricPanelsMap} from './hooks/useMultiMetricPanelsMap';
import {MultiMetricPanelsEmptyState} from './MultiMetricPanelsEmptyState';
import {MultiMetricPanelsList} from './MultiMetricPanelsList';
import {validateMultiMetricQuickAddRegex} from './utils/regex';

type MultiMetricPanelAddProps = {
  regex: string;
  debouncedRegex: string;
  setRegex: (value: string) => void;
};

export const MultiMetricPanelAdd = ({
  regex,
  debouncedRegex,
  setRegex,
}: MultiMetricPanelAddProps) => {
  const {errorMessage: regexValidationError} =
    validateMultiMetricQuickAddRegex(regex);
  // need separate state to track if regex text field should display invalid state.
  // validation error should only appear on blur, and should be cleared on change.
  const [isRegexFieldInvalid, setIsRegexFieldInvalid] = useState(
    Boolean(regexValidationError)
  );

  const {
    map: panelsToAdd,
    numNotAdded,
    numAlreadyAdded,
    numAutoPanelsMatched,
    hasCrossSectionPanels,
    isLoading,
    error,
  } = useMultiMetricPanelsMap(debouncedRegex, !regexValidationError);

  const shouldAutoGeneratePanels = useShouldAutoGeneratePanels();
  const [shouldRemoveAutoPanels, setShouldRemoveAutoPanels] = useState(false);

  const isPanelLimitExceeded = numNotAdded > BULK_ADD_LIMIT_MULTI_METRIC;
  const isDisabled = numNotAdded === 0 || isPanelLimitExceeded;
  const onCreatePanels = useCreateMultiMetricPanels(
    regex,
    panelsToAdd,
    isDisabled,
    shouldRemoveAutoPanels
  );

  return (
    <div className="flex flex-col h-full justify-between">
      <div className="px-24 py-16 flex flex-col gap-24">
        <div data-dd-privacy={DD_PRIVACY_ALLOW}>
          <p className="text-wb-text-tertiary mb-12">
            Use regex with capture groups to quickly generate multi-metric line
            plots.{' '}
            <TargetBlank href={DOC_URLS.multiMetricQuickAdd}>
              Learn more in docs.
            </TargetBlank>
          </p>
          <TextField
            label="Regex"
            value={regex}
            onChange={value => {
              setRegex(value);
              setIsRegexFieldInvalid(false);
            }}
            onBlur={() => {
              setIsRegexFieldInvalid(Boolean(regexValidationError));
            }}
            isInvalid={isRegexFieldInvalid}
            errorMessage={regexValidationError}
            fullWidth
          />
        </div>
        {isPanelLimitExceeded && (
          <Banner variant="warning" isDismissible={false}>
            You may add up to {BULK_ADD_LIMIT_MULTI_METRIC} multi-metric panels
            at once. Refine your regex to limit the results.
          </Banner>
        )}
        {/* The full key set loads eagerly on tab open (even before a regex is
            entered, to prefetch), but with no regex there are no results to
            show, so keep rendering the help/empty state instead of surfacing
            that load's spinner or error until the user has typed a regex. */}
        {debouncedRegex && error != null ? (
          <Banner variant="error" isDismissible={false}>
            Something went wrong loading metrics. Please try again.
          </Banner>
        ) : debouncedRegex && isLoading ? (
          <div className="flex justify-center py-24">
            <InstrumentedWaveLoader
              name="multi-metric-quick-add-keys-loading"
              size="small"
            />
          </div>
        ) : panelsToAdd.size === 0 ? (
          <MultiMetricPanelsEmptyState
            isRegexEmpty={!debouncedRegex}
            isRegexFieldInvalid={isRegexFieldInvalid}
          />
        ) : (
          <>
            <MultiMetricPanelsList
              panelsToAdd={panelsToAdd}
              numNotAdded={numNotAdded}
              numAlreadyAdded={numAlreadyAdded}
              isPanelLimitExceeded={isPanelLimitExceeded}
            />
            {hasCrossSectionPanels && !isDisabled && (
              <Banner variant="info" isDismissible={false}>
                Panels that capture metrics across multiple sections will be
                added to the "{PANEL_BANK_CHARTS_NAME}" section.
              </Banner>
            )}
            {shouldAutoGeneratePanels && numNotAdded > 0 && (
              <Switch
                isSelected={shouldRemoveAutoPanels}
                onChange={setShouldRemoveAutoPanels}
                description="Remove unmodified single-metric line plots that match the regex to reduce clutter and improve workspace performance.">
                <span className="flex items-center gap-6">
                  Clean up auto-generated panels
                  <Counter value={numAutoPanelsMatched} aria-label="panels" />
                </span>
              </Switch>
            )}
          </>
        )}
      </div>
      {!isDisabled && (
        <div className="border-t border-solid border-wb-border p-16">
          <Button fullWidth isDisabled={isDisabled} onPress={onCreatePanels}>
            {`Create ${maybePluralize(numNotAdded, 'panel')}`}
          </Button>
        </div>
      )}
    </div>
  );
};
